import { z } from 'zod';
import { LABEL_CATEGORY } from '@vagusx/shared';

const labelCategorySchema = z.enum([
  LABEL_CATEGORY.DOCUMENT_TYPE,
  LABEL_CATEGORY.WORKFLOW_TAG,
  LABEL_CATEGORY.PROCEDURAL,
]);

/**
 * Classification label response schema.
 */
export const classificationLabelResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  classificationPrompt: z.string().nullable(),
  color: z.string().nullable(),
  isSystem: z.boolean(),
  enabled: z.boolean(),
  triggersExtraction: z.boolean(),
  // 'document_type' | 'workflow_tag' | 'procedural' — string for forward compat
  labelCategory: z.string(),
  sortOrder: z.number().int(),
  rowStatus: z.number().int(),
  createdBy: z.string().uuid().nullable(),
  updatedBy: z.string().uuid().nullable(),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

export type ClassificationLabelResponse = z.infer<typeof classificationLabelResponseSchema>;

/**
 * Create classification label request schema.
 */
export const createClassificationLabelSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(50)
    .regex(/^[a-z0-9_]+$/, 'Name must be lowercase alphanumeric with underscores'),
  displayName: z.string().min(1, 'Display name is required').max(100),
  description: z.string().optional(),
  classificationPrompt: z.string().optional(),
  color: z.string().max(20).optional(),
  labelCategory: labelCategorySchema.optional(),
});

export type CreateClassificationLabelRequest = z.infer<typeof createClassificationLabelSchema>;

/**
 * Update classification label request schema.
 */
export const updateClassificationLabelSchema = z.object({
  displayName: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  classificationPrompt: z.string().nullable().optional(),
  color: z.string().max(20).nullable().optional(),
  enabled: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  labelCategory: labelCategorySchema.optional(),
});

export type UpdateClassificationLabelRequest = z.infer<typeof updateClassificationLabelSchema>;
